<template lang="pug">
  #member-device-management
    WtTitle(h4) Device Management

    .device-status-container(v-if="hasDevice")
      .columns.is-mobile
        .column.is-narrow
          BIcon(
            :type="member.deviceApi === 'Manual entry' ? 'is-grey-light' : 'is-success'"
            icon="check-circle"
            size="is-large"
          )
        .column
          .device-info
            p.device-label Connected Device:
            p.device-name {{ deviceDisplayName }}
            p.device-meta(v-if="lastSync")
              small Last synced: {{ lastSync }}
            p.device-meta(v-else)
              small No sync data available

      .device-actions.mt-4(v-if="member.deviceApi !== 'Manual entry'")
        WtButton(
          color="red"
          size="small"
          @click.native="disconnectDeviceClick"
          :loading="isDisconnecting"
          :disabled="isDisconnecting"
        ) DISCONNECT DEVICE

    .device-status-empty(v-else)
      p.has-text-grey No device connected
      p.is-size-7.has-text-grey-light This member has not connected a device to their account.
</template>

<script>
import { mapActions } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import WtTitle from "@@/WtTitle/WtTitle";
import WtButton from "@@/WtButton/WtButton";

export default {
  name: "MemberDeviceManagement",

  components: { WtTitle, WtButton },

  mixins: [dateUtilityMixin],

  props: {
    member: {
      type: Object,
      required: true
    },

    memberId: {
      type: [String, Number],
      required: true
    }
  },

  data() {
    return {
      isDisconnecting: false,
      apis: {
        fitbit: "fitbit",
        garmin: "garmin",
        googlefit: "googlefit",
        healthConnect: "healthconnect",
        healthkit: "healthkit",
        samsunghealth: "samsunghealth"
      }
    };
  },

  computed: {
    hasDevice() {
      return this.member && this.member.deviceApi;
    },

    deviceDisplayName() {
      if (!this.member || !this.member.deviceApi) return "";

      const deviceApi = this.member.deviceApi.toString().toLowerCase();

      switch (deviceApi) {
        case this.apis.googlefit:
          return "Google Fit";
        case this.apis.healthConnect:
          return "Apple Health Connect";
        case this.apis.healthkit:
          return "Apple Health";
        case this.apis.garmin:
          return "Garmin";
        case this.apis.samsunghealth:
          return "Samsung Health";
        case this.apis.fitbit:
          return this._.get(this.member, "externalDevice.deviceName", "Fitbit");
        default:
          return this.member.deviceApi;
      }
    },

    lastSync() {
      const deviceLastContact = this._.get(
        this.member,
        "externalDevice.deviceLastContact",
        null
      );

      if (!deviceLastContact) return null;

      return this.date(deviceLastContact, "DATE_TIME_FORMAT").formatted;
    }
  },

  methods: {
    ...mapActions("members", ["disconnectMemberDevice"]),
    ...mapActions(["snackBar"]),

    async disconnectDeviceClick() {
      const confirmMessage = `Are you sure you want to disconnect ${this.deviceDisplayName} from ${this.member.username}'s account?`;

      this.$buefy.dialog.confirm({
        message: confirmMessage,
        confirmText: "Disconnect",
        type: "is-warning",
        onConfirm: async () => {
          await this.performDisconnect();
        }
      });
    },

    async performDisconnect() {
      this.isDisconnecting = true;

      try {
        // For admin editing another member, we need to pass the member ID
        // Backend will handle disconnecting the device for that user
        const response = await this.disconnectMemberDevice({
          memberId: this.memberId
        });

        if (response && response.data && response.data.status) {
          await this.snackBar({
            type: "is-success",
            duration: 5000,
            queue: false,
            message:
              response.data.data?.message || "Device disconnected successfully"
          });

          // Emit event to notify parent to refresh member data
          this.$emit("device-disconnected");
        } else {
          await this.snackBar({
            type: "is-danger",
            duration: 5000,
            queue: false,
            message: "Failed to disconnect device"
          });
        }
      } catch (error) {
        await this.snackBar({
          type: "is-danger",
          duration: 5000,
          queue: false,
          message:
            error.response?.data?.data?.message || "Error disconnecting device"
        });
      } finally {
        this.isDisconnecting = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
#member-device-management {
  margin: 1.5rem 0;

  .device-status-container {
    padding: 1rem;
    border-radius: 4px;
    background-color: #f5f5f5;

    .device-info {
      .device-label {
        font-size: 0.875rem;
        color: #7a7a7a;
        margin-bottom: 0.25rem;
      }

      .device-name {
        font-size: 1.25rem;
        font-weight: 600;
        color: #333;
        margin: 0.5rem 0;
      }

      .device-meta {
        color: #999;
        margin-top: 0.5rem;
      }
    }

    .device-actions {
      display: flex;
      gap: 0.5rem;
    }
  }

  .device-status-empty {
    padding: 1.5rem;
    text-align: center;
    border: 1px dashed #e0e0e0;
    border-radius: 4px;
    background-color: #fafafa;
  }
}
</style>
